import React, { useState } from "react";
import api, { errMsg } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/PageHeader";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, LifeBuoy, ShieldCheck, Package, User } from "lucide-react";

const FAQ = [
  { q: "How does Pack Mate work?", a: "Senders post package requests and travellers publish trips. When a traveller offers to deliver (or a sender requests a traveller) and it is accepted, a secure connection is created and you can chat to arrange the hand-over." },
  { q: "Is there any payment on Pack Mate?", a: "Pack Mate focuses on connecting senders and travellers. Any arrangement between users is made directly between them." },
  { q: "How do I stay safe?", a: "Visit our Safety Center for guidance on communication, package inspection, prohibited items, and meeting safely." },
  { q: "What items are not allowed?", a: "Drugs, weapons, cash, hazardous, counterfeit and any illegal goods are strictly prohibited and blocked automatically." },
  { q: "How do I get verified?", a: "Open your Profile and submit for verification. A status badge will show once reviewed. Your ID documents are never shown to other users." },
  { q: "How do I delete my account?", a: "Go to Settings → Danger zone → Delete Account. Deletion may be permanent and some data may be retained where required by law." },
];

const CATEGORIES = ["General", "Account help", "Delivery help", "Safety help", "Report a problem"];

export default function Support() {
  const [form, setForm] = useState({ category: "General", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) { toast.error("Please fill in subject and message"); return; }
    setLoading(true);
    try {
      await api.post("/support", form);
      toast.success("Your request has been sent. We'll get back to you.");
      setForm({ category: "General", subject: "", message: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Support" subtitle="Find answers or reach our team." />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {[{ icon: ShieldCheck, label: "Safety help" }, { icon: Package, label: "Delivery help" }, { icon: User, label: "Account help" }].map((x) => (
          <div key={x.label} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3"><x.icon className="w-5 h-5 text-primary" /><span className="text-sm font-medium text-slate-700">{x.label}</span></div>
        ))}
      </div>

      <SectionCard title="Frequently asked questions" className="mb-6">
        <Accordion type="single" collapsible data-testid="faq-accordion">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-sm font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-slate-500">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </SectionCard>

      <SectionCard title={<span className="flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-primary" /> Contact support</span>}>
        <form onSubmit={submit} className="space-y-4" data-testid="support-form">
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="support-category-select" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Subject</Label><Input data-testid="support-subject-input" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} className="rounded-xl" /></div>
          <div className="space-y-1.5"><Label>Message</Label><Textarea data-testid="support-message-input" value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} className="rounded-xl" rows={4} /></div>
          <Button type="submit" disabled={loading} data-testid="support-submit-btn" className="rounded-xl">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send message"}</Button>
        </form>
      </SectionCard>
    </div>
  );
}
